import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';
import ClientSideConfigSelector from '../../../../recoil/cashout/ClientSideConfigSelector';

const defaultCountries = require('../../../../models/data/countries.json');

export default function CashoutEmailModalContent({ onSubmit, onClose }) {
  const { t } = useTranslation(null, { useSuspense: false });
  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
        providers: {
          [paymentProvider]: {
            display,
            requiresCountryCode = false,
          } = {},
        } = {},
      } = {},
    } = {},
  } = config || {};

  const [disabled, setDisabled] = useState(true);
  const [initiateEmail, setInitiateEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [error, setError] = useState('');

  const countryOptions = useMemo(
    () =>
      // eslint-disable-next-line implicit-arrow-linebreak
      Object.entries(defaultCountries)
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const submitEmailForm = async (evt) => {
    evt.preventDefault();
    if (disabled) return; // extra guard
    onSubmit(confirmEmail, countryCode);
  };

  useEffect(() => {
    if (!initiateEmail || !confirmEmail) {
      setError('');
      setDisabled(true);
      return;
    }

    if (initiateEmail !== confirmEmail) {
      setError(t('cashout_email_address_does_not_match'));
      setDisabled(true);
      return;
    }

    if (requiresCountryCode && !countryCode) {
      setError('');
      setDisabled(true);
      return;
    }

    setError('');
    setDisabled(false);
    // eslint-disable-next-line
  }, [initiateEmail, confirmEmail, countryCode]);

  return (
    <div
      id="cashout-email"
      className="phoenix-simple-dialog-view modal member-diamonds-cash-confirm in show"
      aria-hidden="false"
    >
      <div className="modal-backdrop fade in" />
      <div className="modal-dialog">
        <div className="modal-content modal-content-sw">
          <div className="provider-content" data-provider={paymentProvider}>
            <div className="modal-header">
              <h4>{t('cashout_our_rewards_partner', { provider: display })}</h4>
              <hr />
            </div>

            <form
              id="cashout-email-form"
              className="form-horizontal"
              onSubmit={submitEmailForm}
              noValidate
            >
              <div className="modal-body">
                <div className="form-group">
                  <div className="col-xs-12">
                    {/* First email input */}
                    <div
                      id="diamonds-account-helper-email"
                      className="help-block"
                    >
                      {t('cashout_enter_provider_account_email', { provider: display })}
                    </div>
                    <input
                      id="diamonds-account-email"
                      name="diamonds-account-email"
                      type="email"
                      className="form-control"
                      placeholder={t('cc_email')}
                      value={initiateEmail}
                      onChange={(e) => {
                        setInitiateEmail(e.target.value);
                        if (error) setError('');
                      }}
                      required
                    />
                    {/* Confirm email input */}
                    {initiateEmail && (
                      <>
                        <div
                          id="diamonds-account-helper-email-confirmation"
                          className="help-block"
                        >
                          {t('cashout_confirm_your_email')}
                        </div>

                        <input
                          id="diamonds-account-email-confirmation"
                          name="diamonds-account-email-confirmation"
                          type="email"
                          className="form-control"
                          placeholder={t('cashout_confirm_your_email')}
                          value={confirmEmail}
                          onChange={(e) => {
                            setConfirmEmail(e.target.value);
                            if (error) setError('');
                          }}
                          required
                        />
                      </>
                    )}

                    {/* Country dropdown */}
                    {requiresCountryCode && (
                      <>
                        <div id="diamonds-account-helper-country" className="help-block">
                          {t('cc_country')}
                        </div>
                        <select
                          id="diamonds-account-country-code"
                          name="diamonds-account-country"
                          className="form-control"
                          value={countryCode}
                          onChange={(e) => {
                            setCountryCode(e.target.value);
                            if (error) setError('');
                          }}
                          required
                        >
                          <option value="" disabled> -- </option>
                          {countryOptions.map(({ code, name }) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </>
                    )}

                    {/* Error message */}
                    {error && (
                      <div
                        id="diamonds-account-helper-email-error"
                        className="help-block text-danger"
                      >
                        {error}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="form-group">
                  <div className="col-xs-12">
                    <button
                      type="button"
                      className="btn btn-undesired btn-half btn-cashout-cancel"
                      onClick={onClose}
                    >
                      {t('cashout_cancel')}
                    </button>
                    <button
                      id="submit-cashout-email"
                      type="submit"
                      className="btn btn-primary btn-half btn-cashout-continue"
                      aria-hidden="true"
                      disabled={disabled}
                    >
                      {t('cashout_continue')}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

CashoutEmailModalContent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
